import type {
  BuildingsResponse,
  CreateRenderPaymentIntentRequest,
  CreateRenderPaymentIntentResponse,
  CompareMetrics,
  CompareResponse,
  CreatePaymentIntentRequest,
  EnvironmentalResponse,
  FinalizeRenderRequest,
  FinalizeRenderResponse,
  FulfillRenderRequest,
  FulfillRenderResponse,
  FulfillPaymentRequest,
  FulfillPaymentResponse,
  GeocodeResponse,
  RegenerateRenderRequest,
  RegenerateRenderResponse,
  RendersListResponse,
  ReportGoneError,
  ReportResponse,
  ReportStructuresResponse,
  PutReportMetricsRequest,
  PutReportStructuresRequest,
  ShareSettingsResponse,
  StructuresPayload,
  RoadsResponse,
  SiteRequest,
  SiteResponse,
  SolarPotentialResponse,
  SummaryContext,
  SummaryResponse,
  UtilitiesResponse
} from "@shared/api";
import { getStoredEditSecret } from "../state/reportEditCapability";
import { getSessionToken } from "../state/session";

/**
 * Browser-side client for the parcel-terrain API.
 *
 * Base URL resolution:
 *  - In dev (Vite): `/api/...` (proxied to wrangler dev on :8787)
 *  - In prod: same path. When the SPA and Worker are deployed under the same
 *    Cloudflare zone, a Pages Function or Worker route (`/api/*`) handles
 *    routing. If they're on separate hostnames, set VITE_API_BASE at build
 *    time (e.g. https://parcel-terrain-api.workers.dev/v1).
 *
 * If no base is configured, we default to "/api" — Vite proxies it in dev,
 * and a Cloudflare Pages function or route catches it in prod.
 */
const API_BASE = (
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api"
).replace(/\/+$/, "");

/**
 * Independent base URL used ONLY by the dev-only `/map` dashboard's admin
 * calls (`adminListPurchases`, `adminMapboxToken`). Lets you point the
 * dashboard at the production worker (to see real purchases + the prod
 * Mapbox token) while the rest of the SPA keeps hitting the local
 * `wrangler dev` worker for viewer iteration. Falls back to `API_BASE`
 * when unset, which is the right default for `npm run dev:remote` (where
 * VITE_API_BASE is already prod) and prod builds (where the dashboard
 * is tree-shaken out entirely — see AppRouter.tsx).
 */
const ADMIN_API_BASE = (
  (import.meta.env.VITE_ADMIN_API_BASE as string | undefined) ?? API_BASE
).replace(/\/+$/, "");

export interface ApiErrorBody {
  error: { code: string; message: string; upstream?: string };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly upstream?: string;
  constructor(status: number, body: ApiErrorBody) {
    super(`${body.error.code}: ${body.error.message}`);
    this.status = status;
    this.code = body.error.code;
    this.upstream = body.error.upstream;
    this.name = "ApiError";
  }
}

async function getJson<T>(path: string, params: Record<string, string | number>): Promise<T> {
  const url = new URL(API_BASE + path, window.location.origin);
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    url.searchParams.set(k, String(v));
  }
  let res: Response;
  try {
    res = await fetch(url.toString(), { headers: { accept: "application/json" } });
  } catch (err) {
    throw new Error(
      `Network error contacting API at ${url.pathname}: ${(err as Error).message}`
    );
  }
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(
      `API returned non-JSON (${res.status}) from ${url.pathname}: ${text.slice(0, 200)}`
    );
  }
  if (!res.ok) {
    const errBody = body as ApiErrorBody;
    if (errBody?.error) throw new ApiError(res.status, errBody);
    throw new Error(`HTTP ${res.status} from ${url.pathname}`);
  }
  return body as T;
}

async function postJson<TRes, TBody extends object>(
  path: string,
  body: TBody
): Promise<TRes> {
  return postJsonToUrl<TRes>(
    new URL(API_BASE + path, window.location.origin),
    body
  );
}

/**
 * Lower-level variant of `postJson` that takes a fully-constructed URL
 * instead of a path. Used by routes whose path already embeds dynamic
 * segments (e.g. `/v1/report/{token}/renders/{id}/regenerate`) so the
 * caller doesn't have to bypass URL encoding to thread the segments
 * through a simple-string `path` argument.
 */
async function postJsonToUrl<TRes>(url: URL, body: unknown): Promise<TRes> {
  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json"
      },
      body: JSON.stringify(body)
    });
  } catch (err) {
    throw new Error(
      `Network error contacting API at ${url.pathname}: ${(err as Error).message}`
    );
  }
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(
      `API returned non-JSON (${res.status}) from ${url.pathname}: ${text.slice(0, 200)}`
    );
  }
  if (!res.ok) {
    const errBody = parsed as ApiErrorBody;
    if (errBody?.error) throw new ApiError(res.status, errBody);
    throw new Error(`HTTP ${res.status} from ${url.pathname}`);
  }
  return parsed as TRes;
}

/**
 * Fetch helper for the report structures + share-settings routes. Attaches
 * the two edit-authorization signals the worker accepts, when available:
 *   - `x-report-edit-secret`: the demoted owner capability (localStorage)
 *   - `authorization: Bearer <session>`: a signed-in owner's account session
 * Neither is required for a read (GET); the worker returns `canEdit:false`
 * and a read-only payload when they're absent or don't match.
 */
async function fetchJsonWithEditAuth<TRes>(
  url: URL,
  reportToken: string,
  init: { method: "GET" | "PUT" | "POST"; body?: unknown }
): Promise<TRes> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (init.body !== undefined) headers["content-type"] = "application/json";
  const editSecret = getStoredEditSecret(reportToken);
  if (editSecret) headers["x-report-edit-secret"] = editSecret;
  const session = getSessionToken();
  if (session) headers["authorization"] = `Bearer ${session}`;

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method: init.method,
      headers,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined
    });
  } catch (err) {
    throw new Error(
      `Network error contacting API at ${url.pathname}: ${(err as Error).message}`
    );
  }
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(
      `API returned non-JSON (${res.status}) from ${url.pathname}: ${text.slice(0, 200)}`
    );
  }
  if (!res.ok) {
    const errBody = parsed as ApiErrorBody;
    if (errBody?.error) throw new ApiError(res.status, errBody);
    throw new Error(`HTTP ${res.status} from ${url.pathname}`);
  }
  return parsed as TRes;
}

export const api = {
  /** Geocode an address. Always resolves; an empty `candidates` array means no match. */
  async geocode(query: string, limit = 5): Promise<GeocodeResponse> {
    return getJson<GeocodeResponse>("/v1/geocode", { q: query, limit });
  },

  /**
   * Resolve a site (address OR lat/lon → parcel + DEM URL + metadata).
   * Throws ApiError on 4xx/5xx so callers can branch on `.code`.
   *
   * NOTE: This route burns one Regrid record per call. As of the
   * post-pay Regrid migration the SPA should NOT call `/v1/site` from
   * any pre-payment surface — use `api.preview` for gated viewer
   * loads, marketing scans, and viewer address re-search. The route
   * is kept exported for the paid-report fulfill path (server-side
   * only) and any future admin/debug flow that needs a synchronous
   * Regrid lookup.
   */
  async site(req: SiteRequest): Promise<SiteResponse> {
    const params: Record<string, string | number> = {};
    if (req.address) params.address = req.address;
    if (req.lat != null) params.lat = req.lat;
    if (req.lon != null) params.lon = req.lon;
    if (req.radiusMeters != null) params.radiusMeters = req.radiusMeters;
    return getJson<SiteResponse>("/v1/site", params);
  },

  /**
   * Regrid-free counterpart to `api.site`. Returns the same
   * `SiteResponse` shape but with `parcel: null` always; the DEM URL +
   * resolved center come from Mapbox / Census only. Used by every
   * pre-payment surface (marketing scan, gated viewer boot, viewer
   * address re-search) so unpaid users never consume a Regrid record.
   */
  async preview(req: SiteRequest): Promise<SiteResponse> {
    const params: Record<string, string | number> = {};
    if (req.address) params.address = req.address;
    if (req.lat != null) params.lat = req.lat;
    if (req.lon != null) params.lon = req.lon;
    if (req.radiusMeters != null) params.radiusMeters = req.radiusMeters;
    return getJson<SiteResponse>("/v1/preview", params);
  },

  /**
   * Start a Custom Checkout session for the one-off report. Returns the
   * session client secret (for CheckoutElementsProvider) + session id (for
   * fulfillment after confirm). Replaces the old amount-based PaymentIntent
   * so the charge is attributed to the report Product in Stripe.
   */
  async createReportCheckout(
    req: CreatePaymentIntentRequest
  ): Promise<{ client_secret: string; session_id: string }> {
    return postJson<{ client_secret: string; session_id: string }, CreatePaymentIntentRequest>(
      "/v1/billing/create-report-checkout",
      req
    );
  },

  /**
   * After the Custom Checkout session confirms, run the post-pay
   * resolve -> mint / auto-refund flow keyed by the session id. Same
   * response shape as the legacy fulfill.
   */
  async fulfillReportSession(sessionId: string): Promise<FulfillPaymentResponse> {
    return postJson<FulfillPaymentResponse, { session_id: string }>(
      "/v1/billing/fulfill-report-session",
      { session_id: sessionId }
    );
  },

  /** After PaymentIntent succeeds (redirect-return backstop), mint the report. */
  async fulfillPayment(
    req: FulfillPaymentRequest
  ): Promise<FulfillPaymentResponse> {
    return postJson<FulfillPaymentResponse, FulfillPaymentRequest>(
      "/v1/billing/fulfill",
      req
    );
  },

  /** Start Stripe checkout for the conceptual illustration add-on. */
  async createRenderPaymentIntent(
    req: CreateRenderPaymentIntentRequest
  ): Promise<CreateRenderPaymentIntentResponse> {
    return postJson<
      CreateRenderPaymentIntentResponse,
      CreateRenderPaymentIntentRequest
    >("/v1/billing/create-render-payment-intent", req);
  },

  /** Generate + capture a conceptual illustration purchase. */
  async fulfillRender(
    req: FulfillRenderRequest
  ): Promise<FulfillRenderResponse> {
    return postJson<FulfillRenderResponse, FulfillRenderRequest>(
      "/v1/billing/fulfill-render",
      req
    );
  },

  /**
   * Re-roll an existing render. Server uses the cached screenshot +
   * mask + context on the row, so the client only sends the render id
   * (and optionally an updated `customDetails` note). Server enforces
   * `regenerations_used < regenerations_max` and returns 409 when the
   * cap is reached.
   */
  async regenerateRender(
    token: string,
    req: RegenerateRenderRequest
  ): Promise<RegenerateRenderResponse> {
    const url = new URL(
      `${API_BASE}/v1/report/${encodeURIComponent(token)}/renders/${encodeURIComponent(req.renderId)}/regenerate`,
      window.location.origin
    );
    return postJsonToUrl<RegenerateRenderResponse>(url, {
      customDetails: req.customDetails ?? undefined
    });
  },

  /**
   * Finalize a render — the user has picked the result they're happy
   * with. Server stamps `finalized_at`, queues the receipt email with
   * the final PNG attached, and refuses further re-rolls. Idempotent.
   */
  async finalizeRender(
    token: string,
    req: FinalizeRenderRequest
  ): Promise<FinalizeRenderResponse> {
    const url = new URL(
      `${API_BASE}/v1/report/${encodeURIComponent(token)}/renders/${encodeURIComponent(req.renderId)}/finalize`,
      window.location.origin
    );
    return postJsonToUrl<FinalizeRenderResponse>(url, {});
  },

  /** List conceptual illustrations attached to a paid report token. */
  async listRenders(token: string): Promise<RendersListResponse> {
    const url = new URL(
      `${API_BASE}/v1/report/${encodeURIComponent(token)}/renders`,
      window.location.origin
    );
    let res: Response;
    try {
      res = await fetch(url.toString(), { headers: { accept: "application/json" } });
    } catch (err) {
      throw new Error(
        `Network error contacting API at ${url.pathname}: ${(err as Error).message}`
      );
    }
    const text = await res.text();
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      throw new Error(
        `API returned non-JSON (${res.status}) from ${url.pathname}: ${text.slice(0, 200)}`
      );
    }
    if (!res.ok) {
      const errBody = body as ApiErrorBody;
      if (errBody?.error) throw new ApiError(res.status, errBody);
      throw new Error(`HTTP ${res.status} from ${url.pathname}`);
    }
    return body as RendersListResponse;
  },

  /**
   * Read the server-stored structures for a report, plus whether THIS client
   * is authorized to edit them (the worker evaluates the edit secret /
   * account session / shared-edit flag and returns `canEdit`).
   */
  async getReportStructures(token: string): Promise<ReportStructuresResponse> {
    const url = new URL(
      `${API_BASE}/v1/report/${encodeURIComponent(token)}/structures`,
      window.location.origin
    );
    return fetchJsonWithEditAuth<ReportStructuresResponse>(url, token, {
      method: "GET"
    });
  },

  /**
   * Write the structures list for a report. Sends the edit secret (if this
   * browser holds one) and the account session bearer (if signed in); the
   * worker 403s when neither proves edit access and the report's shared-edit
   * flag is off.
   */
  async putReportStructures(
    token: string,
    structures: StructuresPayload
  ): Promise<ReportStructuresResponse> {
    const url = new URL(
      `${API_BASE}/v1/report/${encodeURIComponent(token)}/structures`,
      window.location.origin
    );
    const reqBody: PutReportStructuresRequest = { structures };
    return fetchJsonWithEditAuth<ReportStructuresResponse>(url, token, {
      method: "PUT",
      body: reqBody
    });
  },

  /**
   * Toggle the owner's "let recipients edit" flag for a report. Owner-only
   * (worker requires the edit secret or owner account session).
   */
  async setShareSettings(
    token: string,
    allowSharedEdit: boolean
  ): Promise<ShareSettingsResponse> {
    const url = new URL(
      `${API_BASE}/v1/report/${encodeURIComponent(token)}/share-settings`,
      window.location.origin
    );
    return fetchJsonWithEditAuth<ShareSettingsResponse>(url, token, {
      method: "POST",
      body: { allowSharedEdit }
    });
  },

  /**
   * Cache the viewer-computed compare metrics for a report (first-write-wins
   * on the server). Same edit auth as structures; the worker 403s
   * `no_active_plan` for free/lapsed reports. Fire-and-forget from the viewer —
   * callers swallow failures since this only backfills a paid-feature cache.
   */
  async putReportMetrics(token: string, metrics: CompareMetrics): Promise<{ ok: true }> {
    const url = new URL(
      `${API_BASE}/v1/report/${encodeURIComponent(token)}/metrics`,
      window.location.origin
    );
    const reqBody: PutReportMetricsRequest = { metrics };
    return fetchJsonWithEditAuth<{ ok: true }>(url, token, {
      method: "PUT",
      body: reqBody
    });
  },

  /** Fetch drivable roads near a point from OSM (cached in R2). */
  async roads(
    lat: number,
    lon: number,
    radiusMeters: number
  ): Promise<RoadsResponse> {
    return getJson<RoadsResponse>("/v1/roads", { lat, lon, radiusMeters });
  },

  /**
   * Fetch the unified Phase 3 environmental envelope (EPA proximity,
   * USFS wildfire, USDA soil, HUD FMR, AirNow AQI) for a point. Each
   * top-level field is either a populated result or null; null means
   * either "no upstream data" or "source disabled in this env" — the
   * latter is reported via the `disabled` block.
   *
   * Cached server-side per source with per-source TTLs; safe to call
   * on every viewer boot.
   */
  async environmental(
    lat: number,
    lon: number
  ): Promise<EnvironmentalResponse> {
    return getJson<EnvironmentalResponse>("/v1/environmental", { lat, lon });
  },

  /** Fetch observed utility infrastructure (hydrants, power lines,
   *  substations, water towers) from OSM (cached in R2). */
  async utilities(
    lat: number,
    lon: number,
    radiusMeters: number
  ): Promise<UtilitiesResponse> {
    return getJson<UtilitiesResponse>("/v1/utilities", {
      lat,
      lon,
      radiusMeters
    });
  },

  /**
   * Fetch annual rooftop solar production estimate from NREL PVWatts v8.
   * Throws `ApiError("solar_disabled", 503)` when the worker has no NREL
   * API key configured — callers should branch on that code to hide the
   * solar row gracefully.
   */
  async solar(
    lat: number,
    lon: number,
    systemCapacityKw?: number
  ): Promise<SolarPotentialResponse> {
    const params: Record<string, string | number> = { lat, lon };
    if (systemCapacityKw != null) params.systemCapacityKw = systemCapacityKw;
    return getJson<SolarPotentialResponse>("/v1/solar", params);
  },

  /** Fetch building footprints from USA Structures (cached in R2). Used
   *  as a fallback when Regrid Premium didn't carry buildings for the
   *  parcel. */
  async buildings(
    lat: number,
    lon: number,
    radiusMeters: number
  ): Promise<BuildingsResponse> {
    return getJson<BuildingsResponse>("/v1/buildings", {
      lat,
      lon,
      radiusMeters
    });
  },

  /**
   * Resolve a paid report by its bearer token.
   *
   * Returns a discriminated union rather than throwing on 410, because
   * the gone-states are part of the expected API surface (every customer
   * eventually hits one) and the SPA needs structured access to the
   * `address` + `expiresAt` echo to render the expired page.
   *
   *   { ok: true,  response }      → render the viewer as normal
   *   { ok: false, gone }          → render the expired/revoked page
   *
   * Token is encoded into the path, not the query string, so it doesn't
   * show up in CORS preflight error messages or referrer logs of any
   * outbound requests the report itself triggers later.
   *
   * Other non-2xx responses (500, 503, network errors) still throw via
   * `ApiError` / `Error` — they're operational failures, not lifecycle
   * states.
   */
  async report(token: string): Promise<ReportResult> {
    const url = new URL(
      `${API_BASE}/v1/report/${encodeURIComponent(token)}`,
      window.location.origin
    );
    let res: Response;
    try {
      res = await fetch(url.toString(), { headers: { accept: "application/json" } });
    } catch (err) {
      throw new Error(
        `Network error contacting API at ${url.pathname}: ${(err as Error).message}`
      );
    }
    const text = await res.text();
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      throw new Error(
        `API returned non-JSON (${res.status}) from ${url.pathname}: ${text.slice(0, 200)}`
      );
    }
    if (res.status === 410) {
      return { ok: false, gone: body as ReportGoneError };
    }
    if (!res.ok) {
      const errBody = body as ApiErrorBody;
      if (errBody?.error) throw new ApiError(res.status, errBody);
      throw new Error(`HTTP ${res.status} from ${url.pathname}`);
    }
    return { ok: true, response: body as ReportResponse };
  },

  /**
   * Generate (or fetch the cached) AI property summary for a paid report.
   *
   * The worker enforces "at most one OpenAI call per purchase" via a
   * D1-row-locked write — repeat calls with the same token return the
   * frozen text without going to OpenAI. Use this only on tokens whose
   * `ReportResponse.summary` came back null on the initial GET.
   *
   * Throws `ApiError` on 4xx/5xx (including the 410 lifecycle states —
   * unlike `report()`, the gone-state branch is rare here because we
   * only call this after a successful report load) and a plain `Error`
   * on network failure.
   */
  async summary(
    token: string,
    context: SummaryContext
  ): Promise<SummaryResponse> {
    const url = new URL(
      `${API_BASE}/v1/report/${encodeURIComponent(token)}/summary`,
      window.location.origin
    );
    let res: Response;
    try {
      res = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "content-type": "application/json",
          accept: "application/json"
        },
        body: JSON.stringify({ context })
      });
    } catch (err) {
      throw new Error(
        `Network error contacting API at ${url.pathname}: ${(err as Error).message}`
      );
    }
    const text = await res.text();
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      throw new Error(
        `API returned non-JSON (${res.status}) from ${url.pathname}: ${text.slice(0, 200)}`
      );
    }
    if (!res.ok) {
      const errBody = body as ApiErrorBody;
      if (errBody?.error) throw new ApiError(res.status, errBody);
      throw new Error(`HTTP ${res.status} from ${url.pathname}`);
    }
    return body as SummaryResponse;
  },

  /**
   * Trigger the self-serve recovery email. Worker always responds 202 on
   * shape-valid input regardless of whether the email matched any rows,
   * so callers should NEVER surface "we have / don't have reports for
   * this address" to the user — it would leak which addresses purchased.
   *
   * The only error this throws is `ApiError` on a 400 (malformed email)
   * or a 429 (rate-limited). Operational 5xx errors throw as usual.
   */
  async recoverReports(email: string): Promise<{ accepted: true }> {
    return postJson<{ accepted: true }, { email: string }>(
      "/v1/report/recover",
      { email }
    );
  }
};

export type ReportResult =
  | { ok: true; response: ReportResponse }
  | { ok: false; gone: ReportGoneError };

/**
 * Sentinel string the Worker returns for "address parsed but unknown" so the
 * UI can distinguish a genuine bad-input error from "we couldn't find it".
 */
export const ADDRESS_NOT_FOUND_CODE = "address_not_found";

/**
 * Wire shape returned by `GET /v1/admin/purchases`. Mirrors the projection
 * built in `worker/src/routes/admin.ts → handleListPurchases`. Kept here
 * (not in `@shared/api`) because it is admin-only and we don't want
 * marketing/viewer code to think it's part of the public contract.
 */
export interface AdminPurchaseRow {
  token: string;
  report_id: string;
  email: string;
  address: string;
  lat: number;
  lon: number;
  county: string | null;
  state: string | null;
  purchased_at: string;
  expires_at: string;
  revoked_at: string | null;
  deleted_at: string | null;
  last_accessed_at: string | null;
  mint_source: string;
  url: string;
}

export interface AdminListPurchasesResponse {
  count: number;
  purchases: AdminPurchaseRow[];
}

async function getJsonWithAdminSecret<T>(
  path: string,
  adminSecret: string,
  searchParams: Record<string, string> = {}
): Promise<T> {
  const url = new URL(`${ADMIN_API_BASE}${path}`, window.location.origin);
  for (const [k, v] of Object.entries(searchParams)) {
    url.searchParams.set(k, v);
  }
  let res: Response;
  try {
    res = await fetch(url.toString(), {
      headers: {
        accept: "application/json",
        "x-admin-secret": adminSecret
      }
    });
  } catch (err) {
    throw new Error(
      `Network error contacting API at ${url.pathname}: ${(err as Error).message}`
    );
  }
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(
      `API returned non-JSON (${res.status}) from ${url.pathname}: ${text.slice(0, 200)}`
    );
  }
  if (!res.ok) {
    const errBody = body as ApiErrorBody;
    if (errBody?.error) throw new ApiError(res.status, errBody);
    throw new Error(`HTTP ${res.status} from ${url.pathname}`);
  }
  return body as T;
}

/**
 * Admin: list purchases. Sends the shared secret in `x-admin-secret`.
 * Surfaces 403 as an `ApiError` so the caller can prompt the user to
 * re-enter the secret. Used by the internal `/map` dashboard.
 */
export async function adminListPurchases(
  adminSecret: string,
  options: { includeInactive?: boolean } = {}
): Promise<AdminListPurchasesResponse> {
  const params: Record<string, string> = {};
  if (options.includeInactive) params.includeInactive = "1";
  return getJsonWithAdminSecret<AdminListPurchasesResponse>(
    "/v1/admin/purchases",
    adminSecret,
    params
  );
}

/**
 * Admin: hand back the worker's Mapbox token so the `/map` dashboard can
 * render a real interactive map. The dashboard caches the token in
 * sessionStorage alongside the admin secret. Throws `ApiError(503)` when
 * the worker has no Mapbox token configured.
 */
export async function adminMapboxToken(adminSecret: string): Promise<string> {
  const body = await getJsonWithAdminSecret<{ token: string }>(
    "/v1/admin/mapbox-token",
    adminSecret
  );
  if (!body.token) {
    throw new Error("Mapbox token endpoint returned empty body");
  }
  return body.token;
}

/**
 * Shape of the metrics block returned by `GET /v1/health` when the admin
 * secret is presented. Keys are flat counter names with the `metrics/`
 * KV prefix already stripped (e.g. `regrid.records.total`,
 * `cache.dem.HIT.month.2026-05`, `summary.generated.2026-05-18`).
 *
 * Bucket suffix conventions (see `worker/src/util/counters.ts`):
 *   - `.<YYYY-MM-DD>`        — UTC day rollup, 90-day TTL
 *   - `.month.<YYYY-MM>`     — America/New_York month rollup, 90-day TTL
 *   - `.total`               — lifetime rollup, no TTL ("running tally")
 *
 * `metricsBuckets.todayUtc` / `monthEastern` are the suffixes the
 * worker is *currently* writing to, so the dashboard can look up the
 * "right now" bucket without recomputing the calendar boundary itself.
 */
export interface AdminMetricsResponse {
  metrics: Record<string, number>;
  buckets: {
    todayUtc: string;
    monthEastern: string;
    nextMonthResetUtc: string;
  };
}

interface HealthEnvelope {
  metrics?: Record<string, number>;
  metricsBuckets?: {
    todayUtc?: string;
    monthEastern?: string;
    nextMonthResetUtc?: string;
  };
}

/**
 * Admin: fetch `/v1/health` with the secret so the response includes the
 * `metrics` + `metricsBuckets` blocks. Throws `ApiError(403)` when the
 * secret is rejected — the same handling pattern as
 * `adminListPurchases` so the caller can surface the "update
 * VITE_ADMIN_SECRET" hint uniformly.
 *
 * The worker returns `metrics`/`metricsBuckets` as siblings of the
 * public health fields; we flatten them into one tidy
 * `AdminMetricsResponse` for the dashboard so the UI never has to dig
 * through the integrations block to find them. If a worker version
 * predates the buckets block (older deploy) we synthesize empty
 * strings, and the dashboard's "this month so far" tile renders a dash
 * instead of crashing.
 */
export async function adminMetrics(
  adminSecret: string
): Promise<AdminMetricsResponse> {
  const body = await getJsonWithAdminSecret<HealthEnvelope>(
    "/v1/health",
    adminSecret
  );
  return {
    metrics: body.metrics ?? {},
    buckets: {
      todayUtc: body.metricsBuckets?.todayUtc ?? "",
      monthEastern: body.metricsBuckets?.monthEastern ?? "",
      nextMonthResetUtc: body.metricsBuckets?.nextMonthResetUtc ?? ""
    }
  };
}

// ─── Accounts / passwordless auth ──────────────────────────────────────────

/**
 * One report row in the customer dashboard. Mirrors the projection in
 * `worker/src/routes/account.ts → projectReport`. `status` is the lifecycle
 * kind ("active" | "expired" | "revoked" | "deleted"); the dashboard only
 * ever receives active + expired rows today.
 */
export interface AccountReportRow {
  report_id: string;
  address: string;
  lat: number;
  lon: number;
  county: string | null;
  state: string | null;
  status: string;
  /** ms epoch */
  purchased_at: number;
  /** ms epoch */
  expires_at: number;
  /** `/viewer?report=<token>` URL — open for active rows. */
  url: string;
  /**
   * Owner's "let recipients edit structures" toggle. When true, anyone with
   * the (view-only) share link can also edit structures; when false, only the
   * owner can. Controlled from the dashboard via `setShareSettings`.
   */
  allow_shared_edit: boolean;
}

/** Plan/quota summary returned alongside the dashboard report list. */
export interface AccountSummary {
  plan: string;
  plan_status: string;
  mints_quota: number;
  mints_used: number;
  current_period_end: number | null;
  seats: number;
  /** True when the plan is set to end at `current_period_end` (won't renew). */
  cancel_at_period_end: boolean;
}

/** The team owner's quota state, as surfaced to members (stored, not live). */
export interface TeamQuotaSummary {
  plan: string;
  plan_status: string;
  mints_quota: number;
  mints_used: number;
  current_period_end: number | null;
}

/**
 * Team affiliation block on the dashboard read. Present only for accounts
 * with a team: owners get a marker (their own `account`/`reports` already
 * ARE the workspace), members get the owner's email + shared quota so the
 * workspace section can render without a second round-trip.
 */
export type AccountTeamInfo =
  | { role: "owner"; active: boolean }
  | {
      role: "member";
      active: boolean;
      owner_email: string;
      account: TeamQuotaSummary;
    };

export interface AccountReportsResponse {
  email: string;
  account: AccountSummary;
  team?: AccountTeamInfo;
  /** Report tokens last chosen for the dashboard "Compare lots" module. */
  compare_selection: string[];
  reports: AccountReportRow[];
}

export interface VerifyLoginResponse {
  session_token: string;
  email: string;
}

/** GET/POST helper that carries the session bearer token. */
async function fetchWithSession<T>(
  path: string,
  sessionToken: string,
  init: { method: "GET" | "POST" | "PUT"; body?: unknown } = { method: "GET" }
): Promise<T> {
  const url = new URL(API_BASE + path, window.location.origin);
  const headers: Record<string, string> = {
    accept: "application/json",
    authorization: `Bearer ${sessionToken}`
  };
  if (init.body !== undefined) headers["content-type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method: init.method,
      headers,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined
    });
  } catch (err) {
    throw new Error(
      `Network error contacting API at ${url.pathname}: ${(err as Error).message}`
    );
  }
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(
      `API returned non-JSON (${res.status}) from ${url.pathname}: ${text.slice(0, 200)}`
    );
  }
  if (!res.ok) {
    const errBody = body as ApiErrorBody;
    if (errBody?.error) throw new ApiError(res.status, errBody);
    throw new Error(`HTTP ${res.status} from ${url.pathname}`);
  }
  return body as T;
}

/**
 * Request a magic-link sign-in email. Always resolves on shape-valid input
 * (the worker 202s regardless of whether the email maps to a customer), so
 * callers must NOT reveal hit-vs-miss to the user. Throws `ApiError` only on
 * 400 (malformed email) or 429 (rate-limited).
 */
export async function requestLoginLink(
  email: string
): Promise<{ accepted: true }> {
  return postJson<{ accepted: true }, { email: string }>(
    "/v1/auth/request-link",
    { email }
  );
}

/** Exchange a magic-link token for a session. Throws `ApiError(401)` when expired. */
export async function verifyLogin(token: string): Promise<VerifyLoginResponse> {
  return postJson<VerifyLoginResponse, { token: string }>("/v1/auth/verify", {
    token
  });
}

/**
 * Email a 6-digit inline sign-in code (the checkout-modal OTP gate). Resolves
 * 202 on accept. Throws `ApiError` on 400 (bad email) or 429 (too many codes
 * requested for this email / IP) — surface those to the user; otherwise treat
 * a resolve as "we sent it if the address is mailable."
 */
export async function requestLoginCode(
  email: string
): Promise<{ accepted: true }> {
  return postJson<{ accepted: true }, { email: string }>(
    "/v1/auth/request-code",
    { email }
  );
}

/**
 * Exchange { email, code } for a session. Throws `ApiError` with code
 * `invalid_code` (401, wrong), `code_expired` (401, stale/used), or
 * `too_many_attempts` (429) so the modal can show targeted copy.
 */
export async function verifyLoginCode(
  email: string,
  code: string
): Promise<VerifyLoginResponse> {
  return postJson<VerifyLoginResponse, { email: string; code: string }>(
    "/v1/auth/verify-code",
    { email, code }
  );
}

/** List every report consolidated under the authenticated account. */
export async function getAccountReports(
  sessionToken: string
): Promise<AccountReportsResponse> {
  return fetchWithSession<AccountReportsResponse>(
    "/v1/account/reports",
    sessionToken
  );
}

/**
 * Batch-read cached compare metrics for an explicit set of report tokens (max
 * 3) — powers the `/compare/lots` page. Each entry is either a usable report
 * (its `metrics` may still be null when never analyzed since the cache
 * shipped) or an `unavailable` stub for a token that isn't found or isn't
 * owned by the requester, so one stale token never fails the others.
 */
export async function getCompareReports(
  sessionToken: string,
  tokens: readonly string[]
): Promise<CompareResponse> {
  const qs = encodeURIComponent(tokens.join(","));
  return fetchWithSession<CompareResponse>(
    `/v1/account/compare?reports=${qs}`,
    sessionToken
  );
}

/** Persist the dashboard compare-lots selection (token list, max 3) on the account. */
export async function saveCompareSelection(
  sessionToken: string,
  tokens: readonly string[]
): Promise<{ ok: true; reports: string[] }> {
  return fetchWithSession("/v1/account/compare-selection", sessionToken, {
    method: "PUT",
    body: { reports: tokens }
  });
}

/** Bind a report (by its bearer token) to the authenticated account. */
export async function claimReport(
  sessionToken: string,
  reportToken: string
): Promise<{ ok: true; claimed: boolean; report_id: string }> {
  return fetchWithSession("/v1/account/claim", sessionToken, {
    method: "POST",
    body: { reportToken }
  });
}

/** Revoke the current session server-side. Best-effort; ignores failures. */
export async function logout(sessionToken: string): Promise<void> {
  await fetchWithSession("/v1/auth/logout", sessionToken, {
    method: "POST",
    body: {}
  });
}

// ─── Subscriptions (Phase 3) ───────────────────────────────────────────────

export type SubscriptionPlan = "pro" | "team";
export type SubscriptionInterval = "monthly" | "annual";

/**
 * Start a Stripe Custom Checkout session for a subscription plan. Returns the
 * session client secret for CheckoutElementsProvider (in-page). Session-gated.
 */
export async function createCheckoutSession(
  sessionToken: string,
  plan: SubscriptionPlan,
  interval: SubscriptionInterval = "monthly"
): Promise<{ client_secret: string; session_id: string }> {
  return fetchWithSession("/v1/billing/create-checkout-session", sessionToken, {
    method: "POST",
    body: { plan, interval }
  });
}

/**
 * Activate a subscription synchronously right after the inline Custom
 * Checkout confirms — writes plan/quota immediately instead of racing the
 * Stripe webhook (and works in local dev where webhooks aren't forwarded).
 * Idempotent with the webhook. Throws `ApiError(409, "subscription_pending")`
 * when Stripe hasn't attached the subscription yet (caller should retry/poll).
 */
export async function fulfillSubscriptionSession(
  sessionToken: string,
  sessionId: string
): Promise<{ plan: string; status: string }> {
  return fetchWithSession(
    "/v1/billing/fulfill-subscription-session",
    sessionToken,
    { method: "POST", body: { session_id: sessionId } }
  );
}

/**
 * Estimate what the Pro → Team upgrade charges TODAY (the prorated
 * difference), so the dashboard can show it before the customer confirms.
 * Same precondition errors as {@link upgradeToTeam}.
 */
export async function previewTeamUpgrade(
  sessionToken: string
): Promise<{ amount_cents: number; currency: string }> {
  return fetchWithSession("/v1/billing/upgrade-preview", sessionToken, {
    method: "POST",
    body: {}
  });
}

/**
 * In-place Pro → Team upgrade on the existing subscription (same billing
 * interval, prorated difference charged immediately). Resets usage so the
 * customer gets a fresh full 30 reports for the rest of the period. Throws
 * `ApiError` with code `upgrade_payment_failed` (402, card declined),
 * `already_team` (409), or `no_upgradable_plan` (409) for targeted copy.
 */
export async function upgradeToTeam(
  sessionToken: string
): Promise<{ plan: string; status: string }> {
  return fetchWithSession("/v1/billing/upgrade-subscription", sessionToken, {
    method: "POST",
    body: {}
  });
}

/** Open the Stripe Billing Portal (manage/cancel). Returns the hosted URL. */
export async function createPortalSession(
  sessionToken: string
): Promise<{ url: string }> {
  return fetchWithSession("/v1/billing/portal-session", sessionToken, {
    method: "POST",
    body: {}
  });
}

export interface MintAccountReportResponse {
  token: string;
  report_id: string;
  url: string;
  /** true if drawn from the monthly quota, false if billed as overage. */
  from_quota: boolean;
  /**
   * Present and true only on the idempotent-renewal path: an active report
   * already existed for this parcel, so the existing one was returned and NO
   * quota/charge was drawn. Absent on a fresh mint.
   */
  reused?: boolean;
}

/**
 * Mint a report against the subscriber's plan quota (or auto-charged overage
 * when quota is spent). A renewal is the same call with an address already in
 * the dashboard. Session-gated; throws `ApiError` 403 (no plan) / 402
 * (overage payment issue).
 */
export async function mintAccountReport(
  sessionToken: string,
  input: { address?: string; lat?: number; lon?: number; radiusMeters?: number }
): Promise<MintAccountReportResponse> {
  return fetchWithSession("/v1/account/mint", sessionToken, {
    method: "POST",
    body: input
  });
}

// ─── Team seats (Team plan) ────────────────────────────────────────────────

export interface TeamMemberEntry {
  account_id: string;
  email: string;
  /** ms epoch */
  joined_at: number;
}

export interface TeamInviteEntry {
  id: string;
  email: string;
  /** ms epoch */
  expires_at: number;
}

/**
 * Role-scoped team state from `GET /v1/team`. Owners get the full roster +
 * seat usage; members only get the owner's email (no roster leak).
 */
export type TeamState =
  | {
      role: "owner";
      active: boolean;
      seats: number;
      seats_used: number;
      members: TeamMemberEntry[];
      invites: TeamInviteEntry[];
    }
  | { role: "member"; active: boolean; owner_email: string };

/**
 * Fetch the caller's team state. Throws `ApiError(404, "no_team")` when the
 * account has no team affiliation — callers typically treat that as "render
 * nothing" rather than an error.
 */
export async function getTeam(sessionToken: string): Promise<TeamState> {
  return fetchWithSession<TeamState>("/v1/team", sessionToken);
}

/**
 * Invite an email to the team (owner only, active Team plan required).
 * Throws `ApiError` with code `seats_full` (409), `already_member` (409),
 * `member_elsewhere` (409), `cannot_invite_self` (400), or
 * `no_active_team_plan` (403) for targeted UI copy.
 */
export async function createTeamInvite(
  sessionToken: string,
  email: string
): Promise<{ ok: true; invite: TeamInviteEntry }> {
  return fetchWithSession("/v1/team/invites", sessionToken, {
    method: "POST",
    body: { email }
  });
}

/** Revoke a pending invite (owner only). Frees the seat immediately. */
export async function revokeTeamInvite(
  sessionToken: string,
  inviteId: string
): Promise<{ ok: true }> {
  return fetchWithSession("/v1/team/invites/revoke", sessionToken, {
    method: "POST",
    body: { inviteId }
  });
}

/** Remove a member from the team (owner only). Their access ends immediately. */
export async function removeTeamMember(
  sessionToken: string,
  accountId: string
): Promise<{ ok: true }> {
  return fetchWithSession("/v1/team/members/remove", sessionToken, {
    method: "POST",
    body: { accountId }
  });
}

/** A workspace report row: dashboard row + who minted it. */
export type TeamReportRow = AccountReportRow & {
  minted_by_email: string | null;
};

export interface TeamReportsResponse {
  owner_email: string;
  account: TeamQuotaSummary;
  reports: TeamReportRow[];
}

/**
 * The shared workspace list: every report anchored to the team owner, with
 * minted-by attribution. Members get 403 `team_inactive` when the owner's
 * plan lapses (the workspace freeze); owners can always read their own.
 */
export async function getTeamReports(
  sessionToken: string
): Promise<TeamReportsResponse> {
  return fetchWithSession<TeamReportsResponse>("/v1/team/reports", sessionToken);
}

/**
 * Mint a report into the team workspace from the shared monthly quota.
 * Same response shape as `mintAccountReport`. Members past the pooled quota
 * get `ApiError(402, "team_quota_exhausted")` — only the owner can mint
 * overage (it charges their card).
 */
export async function mintTeamReport(
  sessionToken: string,
  input: { address?: string; lat?: number; lon?: number; radiusMeters?: number }
): Promise<MintAccountReportResponse> {
  return fetchWithSession("/v1/team/mint", sessionToken, {
    method: "POST",
    body: input
  });
}

export interface AcceptTeamInviteResponse {
  session_token: string;
  email: string;
  owner_email: string;
}

/**
 * Exchange an emailed invite token for a membership + signed-in session
 * (sessionless, like `verifyLogin` — the token is the credential). Throws
 * `ApiError` with code `invite_invalid` (401), `team_inactive` (410), or
 * `seats_full` (409) so the accept page can show targeted copy.
 */
export async function acceptTeamInvite(
  token: string
): Promise<AcceptTeamInviteResponse> {
  return postJson<AcceptTeamInviteResponse, { token: string }>(
    "/v1/team/invites/accept",
    { token }
  );
}
